import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Table } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import UserLayout from '../../components/user/UserLayout';
import api from '../../lib/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function UserAnalyticsPage() {
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState({ daily: [], weekly: [], monthly: [] });
  const [period, setPeriod] = useState('daily');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/user/chat-history/', { params: { page_size: 100 } }),
      api.get('/user/settings/summary/'),
      api.get('/user/settings/analytics/'),
    ])
      .then(([h, s, a]) => {
        setHistory(h.data?.history || []);
        setSummary(s.data?.workspace_summary || null);
        setAnalytics(a.data?.workspace || { daily: [], weekly: [], monthly: [] });
      })
      .catch(() => setMsg('Unable to load analytics data.'));
  }, []);

  const topEmails = useMemo(() => {
    const counts = {};
    history.forEach((item) => {
      const key = item.email || 'guest';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([email, count]) => ({ email, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [history]);

  const rows = analytics?.[period] || [];
  const chartData = {
    labels: rows.map((r) => r.period),
    datasets: [
      { label: 'Chats', data: rows.map((r) => r.chats), borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,.2)', tension: 0.35 },
      { label: 'Payments', data: rows.map((r) => r.payments), borderColor: '#059669', backgroundColor: 'rgba(5,150,105,.2)', tension: 0.35 },
      { label: 'Handovers', data: rows.map((r) => r.handovers), borderColor: '#d97706', backgroundColor: 'rgba(217,119,6,.2)', tension: 0.35 },
    ],
  };

  return (
    <UserLayout title="Analytics" subtitle="Track usage trends, chat volume, and top contacts">
      {msg ? <Alert variant="info">{msg}</Alert> : null}

      <Row className="g-3 mb-4">
        <Col md={3}><Card className="user-stat-card p-3"><div className="user-stat-label">Total Chats</div><div className="user-stat-value">{summary?.chat_messages ?? 0}</div></Card></Col>
        <Col md={3}><Card className="user-stat-card p-3"><div className="user-stat-label">Widgets</div><div className="user-stat-value">{summary?.widgets ?? 0}</div></Card></Col>
        <Col md={3}><Card className="user-stat-card p-3"><div className="user-stat-label">Training Sources</div><div className="user-stat-value">{summary?.training_sources ?? 0}</div></Card></Col>
        <Col md={3}><Card className="user-stat-card p-3"><div className="user-stat-label">Payment Events</div><div className="user-stat-value">{summary?.events ?? 0}</div></Card></Col>
      </Row>

      <Card className="user-card p-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="user-section-title mb-0">Workspace Activity Trend</div>
          <div className="d-flex gap-2">
            {['daily', 'weekly', 'monthly'].map((p) => (
              <button
                key={p}
                type="button"
                className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setPeriod(p)}
              >
                {p[0].toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <Line data={chartData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
      </Card>

      <Card className="user-card p-4 mt-4">
        <div className="user-section-title">Top Contact Emails by Chat Volume</div>
        <div className="table-responsive user-table-wrap">
          <Table hover>
            <thead>
              <tr><th>Email</th><th>Total Chats</th></tr>
            </thead>
            <tbody>
              {topEmails.map((row) => (
                <tr key={row.email}><td>{row.email}</td><td>{row.count}</td></tr>
              ))}
              {!topEmails.length ? <tr><td colSpan={2} className="text-center text-muted py-4">No analytics data yet.</td></tr> : null}
            </tbody>
          </Table>
        </div>
      </Card>
    </UserLayout>
  );
}
